/**
 * @file CFNavigationButton.jsx
 * @description Reusable navigation button component for the sidebar.
 *              Supports routing, click handlers, styling, and expand/collapse states.
 *
 *              Selected-state cues (leaf items):
 *                1. 3px accent bar on the left edge (4px when collapsed)
 *                2. Tinted background in the primary color
 *                3. Bolder label + primary text color
 *                4. Accent-colored start icon
 *
 *              Active-group cue (parent items): when a descendant route is
 *              active, the parent renders bold + primary colored so the whole
 *              group reads as "you are in here".
 *
 * @component CFNavigationButton
 * @exports CFNavigationButton - Navigation button component.
 */

import React from "react";
import { Button, Box, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const CFNavigationButton = ({
    to,
    onClick,
    variant,
    startIcon,
    isSelected,
    isChildActive = false,
    isNavigationExpanded,
    isExpanded,
    children,
    itemName,
    hasChildren = false,
    ...props
}) => {
    const theme = useTheme();

    // Fall back gracefully if the custom `strong` key is not defined on the theme.
    const accent = theme.palette.primary.main;
    const accentStrong =
        theme.palette.primary.strong ||
        theme.palette.primary.dark ||
        theme.palette.primary.main;

    // Only show endIcon if navigation is expanded
    let endIcon = null;
    if (isNavigationExpanded) {
        endIcon = isExpanded ? (
            <KeyboardArrowDownIcon />
        ) : (
            <KeyboardArrowLeftIcon />
        );
    }

    // Parent rows: filled when the parent's own route is active, otherwise
    // outlined. `isChildActive` keeps the outlined shape but strengthens it.
    const parentVariant = isSelected ? "contained" : "outlined";
    const isGroupActive = hasChildren && (isSelected || isChildActive);

    return (
        <Box sx={{ width: "100%" }}>
            <Tooltip
                title={!isNavigationExpanded ? itemName : ""}
                placement="right"
                arrow
            >
                <Button
                    aria-current={isSelected ? "page" : undefined}
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pl: 1.5,
                        pr: 1.5,
                        textTransform: "none",
                        height: "2rem",

                        // ---------- Parent (has children) ----------
                        ...(hasChildren &&
                            isGroupActive &&
                            !isSelected && {
                                color: accentStrong,
                                fontWeight: 600,
                                borderColor: accent,
                                "&:hover": {
                                    borderColor: accent,
                                },
                            }),

                        // ---------- Leaf (no children) ----------
                        ...(!hasChildren && {
                            opacity: ".85",
                            transition:
                                "opacity 200ms ease-in-out, background-color 200ms ease-in-out",
                            fontSize: ".75rem",
                            borderRadius: 1,
                            ...(isSelected
                                ? {
                                      opacity: 1,
                                      color: accentStrong,
                                      fontWeight: 600,
                                      backgroundColor: alpha(accent, 0.12),
                                      "& .MuiButton-startIcon": {
                                          color: accent,
                                      },
                                      // Left accent bar — the cue that survives
                                      // the collapsed rail, where labels are hidden.
                                      "&::before": {
                                          content: '""',
                                          position: "absolute",
                                          left: 0,
                                          top: 4,
                                          bottom: 4,
                                          width: isNavigationExpanded
                                              ? "3px"
                                              : "4px",
                                          borderRadius: "0 3px 3px 0",
                                          backgroundColor: accent,
                                      },
                                      "&:hover": {
                                          backgroundColor: alpha(accent, 0.18),
                                      },
                                  }
                                : {
                                      color: "#444444",
                                      fontWeight: 400,
                                      "&:hover": {
                                          opacity: 1,
                                      },
                                  }),
                        }),
                    }}
                    component={Link}
                    to={to}
                    onClick={onClick}
                    fullWidth
                    startIcon={startIcon}
                    endIcon={hasChildren && endIcon}
                    size="small"
                    variant={hasChildren ? parentVariant : null}
                    {...props}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexGrow: 1,
                            justifyContent: "flex-start",
                        }}
                    >
                        {isNavigationExpanded && itemName}
                    </Box>
                </Button>
            </Tooltip>
        </Box>
    );
};

export default CFNavigationButton;